import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    RefreshCw, Users, Clock, Wallet, TrendingUp, MapPin, BedDouble,
    Building2, Sparkles, Target,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchTargetingBrief, fetchTargetingBriefList } from '../api/analyticsApi';
import EngagementListModal from './EngagementListModal';

// $1,725,000 -> "$1.73M"; $620,000 -> "$620K"; null -> "—"
const fmtPrice = (n) => {
    if (n == null) return '—';
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, '')}M`;
    if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
    return `$${n.toLocaleString()}`;
};

const FINANCE_ACCENT = {
    cash: '#d97706',
    pre_approved: '#0e9f6e',
    broker: '#2563eb',
    not_ready: '#9ca3af',
};
const BUYER_TYPE_ACCENT = {
    investor: '#2563eb',
    relocating: '#0891b2',
    'first home buyer': '#d97706',
    downsizer: '#7c3aed',
    upsizer: '#0e9f6e',
    developer: '#dc2626',
    'owner occupier': '#0d9488',
    rentvestor: '#c026d3',
};

const Chip = ({ children, color = '#6b7280' }) => (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
          style={{ background: `${color}1a`, color }}>
        {children}
    </span>
);

const Card = ({ title, Icon, iconColor = '#2563eb', children, subtitle, action }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
        {title && (
            <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4" style={{ color: iconColor }} />} {title}
                </h3>
                {action}
            </div>
        )}
        {subtitle && <p className="text-xs text-gray-400 mb-3">{subtitle}</p>}
        <div className={subtitle || title ? '' : ''}>{children}</div>
    </div>
);

// A headline stat that opens a buyer drill-down.
const StatTile = ({ label, value, sub, color, onClick }) => {
    const clickable = !!onClick && value > 0;
    return (
        <button
            type="button"
            onClick={clickable ? onClick : undefined}
            disabled={!clickable}
            className={`text-left bg-white rounded-xl border border-gray-200 p-4 w-full transition-all ${
                clickable ? 'hover:shadow-md hover:border-gray-300 cursor-pointer' : 'cursor-default'
            }`}
        >
            <div className="text-3xl font-bold" style={{ color }}>{(value ?? 0).toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1 leading-snug">{label}</div>
            {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
        </button>
    );
};

// Each bar drills into the buyers behind it when the row carries a
// `segment_key` (Christian, Monday 12759048834: "make these buttons clickable
// to see which buyers interested").
const DemandBars = ({ items = [], accent = '#2563eb', labelKey = 'label', valueKey = 'count', onItem }) => {
    const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-3 text-center">No data recorded yet.</p>;
    }
    return (
        <div className="space-y-2.5">
            {items.map((item) => {
                const count = item[valueKey] || 0;
                const clickable = !!onItem && !!item.segment_key && count > 0;
                return (
                    <button
                        key={item[labelKey]}
                        type="button"
                        onClick={clickable ? () => onItem(item) : undefined}
                        disabled={!clickable}
                        title={clickable
                            ? `View the ${count} buyer${count === 1 ? '' : 's'} who want ${item[labelKey]}`
                            : undefined}
                        className={`group w-full flex items-center gap-3 text-left rounded-md px-1 -mx-1 py-0.5 transition-colors ${
                            clickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                        }`}
                    >
                        <span className="text-xs text-gray-600 w-28 flex-shrink-0 truncate group-hover:text-gray-900"
                              title={item[labelKey]}>
                            {item[labelKey]}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div className="h-4 rounded-full transition-all group-hover:opacity-80"
                                 style={{ width: `${Math.max(6, (count / max) * 100)}%`, background: accent }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-900 w-10 text-right">
                            {count.toLocaleString()}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

const SuburbCard = ({ s, advice, onOpenSuburb }) => (
    <button
        type="button"
        onClick={() => onOpenSuburb(s)}
        title={`View the ${s.buyer_count} buyers targeting ${s.suburb}`}
        className="group text-left bg-white rounded-xl border border-gray-200 p-4 flex flex-col w-full cursor-pointer transition-all hover:shadow-md hover:border-blue-300"
    >
        <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
                <div className="text-[10px] font-mono text-gray-400 mb-0.5">
                    #{String(s.rank).padStart(2, '0')} · {s.buyer_count} buyer{s.buyer_count === 1 ? '' : 's'}
                </div>
                <span className="block text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                    {s.suburb}
                </span>
            </div>
            {s.within_90d > 0 && (
                <Chip color="#d97706"><Clock className="w-3 h-3" /> {s.within_90d} ≤90d</Chip>
            )}
        </div>

        {s.price_band && (
            <div className="text-sm font-semibold text-emerald-700 mt-2">
                {fmtPrice(s.price_band.min)}–{fmtPrice(s.price_band.max)}
                <span className="text-[10px] font-normal text-gray-400 ml-1">buyer budgets (median)</span>
            </div>
        )}

        {s.property_types?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5">
                {s.property_types.map((pt) => (
                    <Chip key={pt.label} color="#2563eb">{pt.label} · {pt.count}</Chip>
                ))}
            </div>
        )}

        {s.bedrooms?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
                {s.bedrooms.map((bd) => (
                    <Chip key={bd.label} color="#7c3aed">
                        <BedDouble className="w-3 h-3" /> {bd.label} · {bd.count}
                    </Chip>
                ))}
            </div>
        )}

        {s.buyer_types?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
                {s.buyer_types.map((bt) => (
                    <Chip key={bt.key} color={BUYER_TYPE_ACCENT[bt.key] || '#6b7280'}>
                        {bt.label} · {bt.count}
                    </Chip>
                ))}
            </div>
        )}

        {advice && (
            <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600 leading-relaxed">
                <span className="inline-flex items-center gap-1 font-semibold text-blue-600">
                    <Sparkles className="w-3 h-3" /> Source here
                </span>
                <span className="ml-1">{advice}</span>
            </div>
        )}
    </button>
);

// "Property goals", the single shared Buying-Preferences question, surfaced
// on the Buyer Analytics tab (moved here from User Profiles). Each goal is a
// clickable bar drilling into the buyers who picked it.
const InvestmentGoals = ({ goals, onOption }) => {
    const accent = '#0e9f6e';
    const answered = goals?.answered || 0;
    const options = (goals?.options || []).filter((o) => o.count > 0);
    if (answered === 0 || options.length === 0) {
        return <p className="text-xs text-gray-400 py-3 text-center">No property goals recorded yet.</p>;
    }
    const max = Math.max(1, ...options.map((o) => o.count || 0));
    return (
        <div className="space-y-3">
            {options.map((opt) => {
                const pct = Math.round(((opt.count || 0) / answered) * 100);
                return (
                    <button
                        key={opt.segment_key}
                        type="button"
                        onClick={() => onOption(opt)}
                        title={`View the ${opt.count} buyer${opt.count === 1 ? '' : 's'} with this goal`}
                        className="w-full text-left group"
                    >
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 group-hover:text-emerald-700 transition-colors">
                                {opt.value}
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                                {opt.count} · {pct}%
                            </span>
                        </div>
                        <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div className="h-2 rounded-full transition-all"
                                 style={{ width: `${Math.max(4, ((opt.count || 0) / max) * 100)}%`, background: accent }} />
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

const AdviceNote = ({ text }) => {
    if (!text) return null;
    return (
        <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
            <Sparkles className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-900 leading-relaxed">{text}</p>
        </div>
    );
};

const TargetingBriefPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);
    const [listModal, setListModal] = useState({ open: false, loading: false, title: '', subtitle: '', rows: [] });

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchTargetingBrief(startDate, endDate)
            .then((d) => { if (myToken === loadTokenRef.current) setData(d); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error(err);
                toast.error('Failed to load targeting brief');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    const openSegment = useCallback(async (segment, title, count) => {
        setListModal({ open: true, loading: true, title, subtitle: `${count} buyer${count === 1 ? '' : 's'}`, rows: [] });
        try {
            const res = await fetchTargetingBriefList(startDate, endDate, segment);
            setListModal((m) => ({ ...m, loading: false, rows: res?.rows || [] }));
        } catch (err) {
            console.error('Targeting drill-down failed:', err);
            toast.error('Failed to load list');
            setListModal((m) => ({ ...m, loading: false, rows: [] }));
        }
    }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-20 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading targeting brief…
            </div>
        );
    }

    const h = data?.headline || {};
    const advice = data?.advice || null;
    const inv = data?.investment || {};
    const total = h.total_buyers || 0;
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

    return (
        <div className="space-y-6">
            {/* Intro */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Target className="w-4 h-4 text-gray-400" />
                    <span>
                        <strong className="text-gray-900">{total.toLocaleString()}</strong> buyers with active
                        buying preferences — what to source and who to match it to.
                    </span>
                </div>
                {data?.advice_generated_at ? (
                    <span className="text-[11px] text-gray-400">
                        Advice updated {new Date(data.advice_generated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                    </span>
                ) : (
                    <span className="text-[11px] text-gray-400">Numbers live · AI advice pending first nightly run</span>
                )}
            </div>

            {/* Urgency strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile label="Buyers with preferences" value={total} color="var(--c-on-strong)"
                          onClick={() => openSegment('total_buyers', 'Buyers with preferences', total)} />
                <StatTile label="Buying within 90 days" sub={`${pct(h.buying_within_90d)}% of buyers`}
                          value={h.buying_within_90d} color="#0e9f6e"
                          onClick={() => openSegment('buying_within_90d', 'Buying within 90 days', h.buying_within_90d)} />
                <StatTile label="Finance-ready (cash / pre-approved)" sub={`${pct(h.finance_ready)}% of buyers`}
                          value={h.finance_ready} color="#d97706"
                          onClick={() => openSegment('finance_ready', 'Finance-ready buyers', h.finance_ready)} />
                <StatTile label="Confirmed investors" sub={`${pct(h.investors)}% of buyers`}
                          value={h.investors} color="#2563eb"
                          onClick={() => openSegment('investors', 'Investors', h.investors)} />
            </div>

            {advice?.summary && <AdviceNote text={advice.summary} />}

            {/* Priority suburbs */}
            <Card title="Priority suburbs — ranked by buyer demand" Icon={MapPin}
                  subtitle="Where buyers want to buy, the types they want, their own budget bands, and which buyer types to match · click a suburb for the buyer list">
                {data?.suburbs?.length ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {data.suburbs.map((s) => (
                            <SuburbCard
                                key={s.suburb}
                                s={s}
                                advice={advice?.suburbs?.[s.suburb]}
                                onOpenSuburb={(sub) => openSegment(`suburb:${sub.suburb}`, `Buyers targeting ${sub.suburb}`, sub.buyer_count)}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="text-xs text-gray-400 py-4 text-center">No suburb preferences recorded yet.</p>
                )}
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Investment benchmark */}
                <Card title="Investment benchmark — portal calculator averages" Icon={TrendingUp}
                      subtitle={`What investors are actually modelling${inv.sample_size ? ` · ${inv.sample_size} valid calculations` : ''}`}>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-amber-600">{inv.avg_gross_yield_pct ?? '—'}{inv.avg_gross_yield_pct != null ? '%' : ''}</div>
                            <div className="text-[11px] text-gray-500">Avg gross yield</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-emerald-600">{inv.avg_net_yield_pct ?? '—'}{inv.avg_net_yield_pct != null ? '%' : ''}</div>
                            <div className="text-[11px] text-gray-500">Avg net yield</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-gray-900">{inv.avg_monthly_cash_flow != null ? fmtPrice(Math.round(inv.avg_monthly_cash_flow)) : '—'}</div>
                            <div className="text-[11px] text-gray-500">Avg monthly cashflow</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-2xl font-bold text-gray-900">{fmtPrice(inv.avg_price)}</div>
                            <div className="text-[11px] text-gray-500">Avg modelled price</div>
                        </div>
                    </div>
                    <AdviceNote text={advice?.investors} />
                </Card>

                {/* Finance readiness */}
                <Card title="Finance readiness — call these first" Icon={Wallet} iconColor="#d97706"
                      subtitle="Self-reported in the portal or set by an agent · click for the list">
                    <div className="grid grid-cols-2 gap-3">
                        {(data?.finance || []).map((f) => {
                            const clickable = f.count > 0;
                            return (
                                <button
                                    key={f.key}
                                    type="button"
                                    disabled={!clickable}
                                    onClick={clickable ? () => openSegment(`finance:${f.key}`, f.label, f.count) : undefined}
                                    className={`text-left bg-gray-50 rounded-lg p-3 transition-all ${clickable ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'}`}
                                >
                                    <div className="text-2xl font-bold" style={{ color: FINANCE_ACCENT[f.key] || '#111827' }}>
                                        {(f.count || 0).toLocaleString()}
                                    </div>
                                    <div className="text-[11px] text-gray-500">{f.label}</div>
                                </button>
                            );
                        })}
                    </div>
                    <AdviceNote text={advice?.finance} />
                </Card>
            </div>

            {/* Buyer types */}
            <Card title="Buyer types — match opportunities to who's looking" Icon={Users}
                  subtitle="From buying preferences (+ CRM investor flag) · click for the list">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(data?.buyer_types || []).map((bt) => {
                        const clickable = bt.count > 0;
                        return (
                            <button
                                key={bt.key}
                                type="button"
                                disabled={!clickable}
                                onClick={clickable ? () => openSegment(`buyer_type:${bt.key}`, bt.label, bt.count) : undefined}
                                className={`text-left bg-white rounded-lg border border-gray-200 p-3 transition-all ${clickable ? 'hover:shadow-md hover:border-gray-300 cursor-pointer' : 'cursor-default'}`}
                            >
                                <div className="text-xl font-bold" style={{ color: BUYER_TYPE_ACCENT[bt.key] || '#111827' }}>
                                    {(bt.count || 0).toLocaleString()}
                                </div>
                                <div className="text-[11px] text-gray-500 leading-tight">{bt.label}</div>
                            </button>
                        );
                    })}
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Property type demand */}
                <Card title="Property type demand" Icon={Building2}
                      subtitle="Distinct buyers wanting each type · click a type for the buyer list">
                    <DemandBars
                        items={data?.property_types} accent="#2563eb"
                        onItem={(it) => openSegment(it.segment_key, `Buyers wanting ${it.label}`, it.count)}
                    />
                </Card>

                {/* Bedroom preferences */}
                <Card title="Bedroom preferences" Icon={BedDouble} iconColor="#7c3aed"
                      subtitle={`${data?.any_bedroom || 0} of ${total} set "any" — don't pre-filter most buyers by bedroom count · click a row for the buyer list`}
                      action={data?.any_bedroom > 0 && (
                          <button
                              type="button"
                              onClick={() => openSegment('bedroom:any', 'Buyers with no bedroom preference', data.any_bedroom)}
                              className="text-[11px] font-semibold text-purple-600 hover:underline flex-shrink-0"
                          >
                              See the “any” buyers
                          </button>
                      )}>
                    <DemandBars
                        items={data?.bedrooms} accent="#7c3aed"
                        onItem={(it) => openSegment(it.segment_key, `Buyers wanting ${it.label}`, it.count)}
                    />
                </Card>
            </div>

            {/* Property goals, the single shared Buying-Preferences question */}
            <Card title="Property goals" Icon={Target} iconColor="#0e9f6e"
                  subtitle={`What is most important to them · from the Buying Preferences form${
                      data?.investment_goals?.answered ? ` · ${data.investment_goals.answered} respondent${data.investment_goals.answered === 1 ? '' : 's'}` : ''
                  } · click a goal for the buyer list`}>
                <InvestmentGoals
                    goals={data?.investment_goals}
                    onOption={(opt) => openSegment(opt.segment_key, `Property goal: ${opt.value}`, opt.count)}
                />
            </Card>

            <EngagementListModal
                isOpen={listModal.open}
                onClose={() => setListModal((m) => ({ ...m, open: false }))}
                title={listModal.title}
                subtitle={listModal.subtitle}
                loading={listModal.loading}
                rows={listModal.rows}
                variant="buyers"
            />
        </div>
    );
};

export default TargetingBriefPanel;
